import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import api from '../services/api';
import CourseSearchPicker from './CourseSearchPicker';

interface ClientFormData {
  name: string;
  email: string;
}

interface User {
  _id: string;
  name: string;
  email: string;
  role: string;
}

interface Collaborator {
  user: string | { _id: string; name: string; email: string };
  status: 'pending' | 'accepted' | 'declined';
  invitedAt: string;
}

interface VisibleDesigner {
  _id: string;
  name: string;
  email: string;
}

interface Project {
  _id: string;
  name: string;
  clients?: string[];
  designer?: string;
  location?: { latitude: number; longitude: number };
  trackingEnabled?: boolean;
  managementEnabled?: boolean;
  collaborators?: Collaborator[];
  myRole?: 'owner' | 'collaborator' | 'pending' | 'admin';
  createdAt: string;
}

const ProjectScreen: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);

  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [location, setLocation] = useState<{ latitude: number; longitude: number } | undefined>();
  const [trackingEnabled, setTrackingEnabled] = useState(false);
  const [managementEnabled, setManagementEnabled] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingLocation, setEditingLocation] = useState<{ latitude: number; longitude: number } | undefined>();
  const [editingTracking, setEditingTracking] = useState(false);
  const [editingManagement, setEditingManagement] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [selectedProjectForClient, setSelectedProjectForClient] = useState<Project | null>(null);
  const [clientForm, setClientForm] = useState<ClientFormData>({
    name: '',
    email: '',
  });
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [selectedExistingUser, setSelectedExistingUser] = useState<User | null>(null);
  const [searching, setSearching] = useState(false);
  const [addingClient, setAddingClient] = useState(false);
  const [deletingProjectId, setDeletingProjectId] = useState<string | null>(null);

  // Collaborator state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteProjectId, setInviteProjectId] = useState<string | null>(null);
  const [visibleDesigners, setVisibleDesigners] = useState<VisibleDesigner[]>([]);
  const [loadingDesigners, setLoadingDesigners] = useState(false);
  const [inviting, setInviting] = useState<string | null>(null);
  const [removingCollaborator, setRemovingCollaborator] = useState<string | null>(null);
  const [respondingProject, setRespondingProject] = useState<string | null>(null);

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) {
      alert('Please enter a project name');
      return;
    }

    setCreating(true);
    try {
      const projectData: any = {
        name: newProjectName.trim(),
        trackingEnabled,
        managementEnabled,
      };

      // Add location if provided
      if (location) {
        projectData.location = location;
      }

      await api.post('/projects', projectData);
      setShowCreateModal(false);
      setNewProjectName('');
      setLocation(undefined);
      setTrackingEnabled(false);
      setManagementEnabled(false);
      fetchProjects();
    } catch (error: any) {
      console.error('Failed to create project:', error);
      alert(`Failed to create project: ${error.response?.data?.error || 'Unknown error'}`);
    } finally {
      setCreating(false);
    }
  };

  const handleProjectClick = (project: Project) => {
    navigate(`/project/${project._id}`);
  };

  const handleAddClient = async () => {
    if (!selectedProjectForClient || !clientForm.name.trim() || !clientForm.email.trim()) {
      alert('Please fill in all fields');
      return;
    }

    setAddingClient(true);
    try {
      // Pass projectId so the backend assigns both sides atomically
      const res = await api.post('/auth/register-client', {
        ...clientForm,
        projectId: selectedProjectForClient._id,
      });
      const client = res.data;

      // (Optional safety) Ensure membership is set – the server already does this with $addToSet
      await api.post(`/projects/${selectedProjectForClient._id}/clients`, {
        userId: client.userId,
      });

      // Clean up UI
      setShowClientModal(false);
      setClientForm({ name: '', email: '' });
      setSelectedProjectForClient(null);
      fetchProjects();
    } catch (err: any) {
      console.error('Failed to add client:', err);
      alert(`Failed to add client: ${err.response?.data?.error || 'Unknown error'}`);
    } finally {
      setAddingClient(false);
    }
  };

  const openAddClient = (project: Project) => {
    setSelectedProjectForClient(project);
    setClientForm({ name: '', email: '' });
    setSelectedExistingUser(null);
    loadAvailableUsers(project);
    setShowClientModal(true);
  };

  const loadAvailableUsers = async (project: Project) => {
    try {
      setSearching(true);
      const response = await api.get('/users/clients');

      // Filter out users already assigned to this project
      const availableUsers = response.data.filter((u: User) =>
        !project.clients?.includes(u._id)
      );

      setAvailableUsers(availableUsers);
    } catch (error) {
      console.error('Error loading available users:', error);
      setAvailableUsers([]);
    } finally {
      setSearching(false);
    }
  };

  const handleAddExistingUser = async () => {
    if (!selectedProjectForClient || !selectedExistingUser) {
      alert('Please select a client to add');
      return;
    }

    setAddingClient(true);
    try {
      await api.post(`/projects/${selectedProjectForClient._id}/clients`, {
        userId: selectedExistingUser._id,
      });

      setShowClientModal(false);
      setSelectedProjectForClient(null);
      setClientForm({ name: '', email: '' });
      setAvailableUsers([]);
      setSelectedExistingUser(null);
      fetchProjects();
    } catch (err: any) {
      console.error('Failed to add existing client:', err);
      alert(`Failed to add client: ${err.response?.data?.error || 'Unknown error'}`);
    } finally {
      setAddingClient(false);
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      return;
    }

    setDeletingProjectId(projectId);
    try {
      await api.delete(`/projects/${projectId}`);
      fetchProjects();
      alert('Project deleted successfully');
    } catch (error: any) {
      console.error('Failed to delete project:', error);
      alert(`Failed to delete project: ${error.response?.data?.error || 'Unknown error'}`);
    } finally {
      setDeletingProjectId(null);
    }
  };

  const openEditProject = async (project: Project) => {
    setEditingProject(project);
    setEditingName(project.name);
    setEditingLocation(project.location);
    setEditingTracking(project.trackingEnabled || false);
    setEditingManagement(project.managementEnabled || false);
    setShowEditModal(true);

    // Fetch the full project so collaborator user objects are populated
    try {
      const response = await api.get(`/projects/${project._id}`);
      setEditingProject(response.data);
    } catch (error) {
      console.error('Failed to load project details:', error);
    }
  };

  const handleEditProject = async () => {
    if (!editingProject || !editingName.trim()) {
      alert('Please enter a project name');
      return;
    }

    setEditing(true);
    const updateData: any = {
      name: editingName.trim(),
      trackingEnabled: editingTracking,
      managementEnabled: editingManagement,
    };

    if (editingLocation) {
      updateData.location = editingLocation;
    }

    try {
      await api.put(`/projects/${editingProject._id}`, updateData);
      setShowEditModal(false);
      setEditingProject(null);
      fetchProjects();
      alert('Project updated successfully');
    } catch (error: any) {
      if (error.response?.data?.requiresConfirmation) {
        const confirmed = window.confirm(
          error.response.data.message ||
          'Disabling project tracking will delete all progress updates. Continue?'
        );
        if (confirmed) {
          try {
            await api.put(`/projects/${editingProject._id}`, {
              ...updateData,
              confirmDeleteTracking: true,
            });
            setShowEditModal(false);
            setEditingProject(null);
            fetchProjects();
            alert('Project updated successfully');
          } catch (confirmError: any) {
            console.error('Failed to update project:', confirmError);
            alert(`Failed to update project: ${confirmError.response?.data?.error || 'Unknown error'}`);
          }
        }
      } else if (error.response?.data?.requiresConfirmationManagement) {
        const confirmed = window.confirm(
          error.response.data.message ||
          'Disabling project management will delete all phase data. Continue?'
        );
        if (confirmed) {
          try {
            await api.put(`/projects/${editingProject._id}`, {
              ...updateData,
              confirmDeleteManagement: true,
            });
            setShowEditModal(false);
            setEditingProject(null);
            fetchProjects();
            alert('Project updated successfully');
          } catch (confirmError: any) {
            console.error('Failed to update project:', confirmError);
            alert(`Failed to update project: ${confirmError.response?.data?.error || 'Unknown error'}`);
          }
        }
      } else {
        console.error('Failed to update project:', error);
        alert(`Failed to update project: ${error.response?.data?.error || 'Unknown error'}`);
      }
    } finally {
      setEditing(false);
    }
  };

  const openInviteModal = async (projectId: string) => {
    setInviteProjectId(projectId);
    setLoadingDesigners(true);
    setShowInviteModal(true);
    try {
      const res = await api.get(`/users/designers/visible?projectId=${projectId}`);
      setVisibleDesigners(res.data);
    } catch {
      setVisibleDesigners([]);
    } finally {
      setLoadingDesigners(false);
    }
  };

  const handleInviteDesigner = async (userId: string) => {
    if (!inviteProjectId) return;
    setInviting(userId);
    try {
      await api.post(`/projects/${inviteProjectId}/collaborators`, { userId });
      setShowInviteModal(false);
      fetchProjects();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to send invitation');
    } finally {
      setInviting(null);
    }
  };

  const handleRemoveCollaborator = async (projectId: string, userId: string, name: string) => {
    if (!window.confirm(`Remove ${name} as a collaborator on this project?`)) return;
    setRemovingCollaborator(userId);
    try {
      await api.delete(`/projects/${projectId}/collaborators/${userId}`);
      fetchProjects();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to remove collaborator');
    } finally {
      setRemovingCollaborator(null);
    }
  };

  const handleRespondToInvitation = async (projectId: string, accept: boolean) => {
    setRespondingProject(projectId);
    try {
      await api.put(`/projects/${projectId}/collaborators/respond`, { accept });
      fetchProjects();
    } catch (err: any) {
      const msg = err.response?.data?.message || err.response?.data?.error || 'Failed to respond';
      alert(msg);
    } finally {
      setRespondingProject(null);
    }
  };

  // Derived lists
  const pendingInvites = projects.filter((p) => p.myRole === 'pending');
  const activeProjects = projects.filter((p) => p.myRole !== 'pending');

  // Subscription limit check for accepting invitations
  const ownedCount = projects.filter((p) => p.myRole === 'owner').length;
  const collabCount = projects.filter((p) => p.myRole === 'collaborator').length;
  const projectLimit =
    user?.subscriptionTier === 'one_project' ? 1
    : user?.subscriptionTier === 'two_projects' ? 2
    : Infinity;
  const atCollabLimit = user?.role === 'designer' && (ownedCount + collabCount) >= projectLimit;

  const getUserId = (collab: Collaborator): string =>
    typeof collab.user === 'string' ? collab.user : collab.user._id;

  const getUserName = (collab: Collaborator): string =>
    typeof collab.user === 'string' ? 'Unknown' : collab.user.name;

  if (loading) {
    return (
      <div className="min-h-screen bg-blue-50 flex items-center justify-center">
        <div className="text-lg">Loading projects...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-blue-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex justify-between items-start mb-6">
          <h1 className="text-2xl font-bold">{user?.role === 'admin' ? 'All Projects' : 'Project Management'}</h1>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/')}
                className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
              >
                Back to Dashboard
              </button>
              {(user?.role === 'admin' || user?.role === 'designer') && (() => {
                const projectLimit =
                  user?.subscriptionTier === 'one_project' ? 1
                  : user?.subscriptionTier === 'two_projects' ? 2
                  : Infinity;
                const atLimit = user?.role === 'designer' && projects.length >= projectLimit;
                return atLimit ? (
                  <button
                    disabled
                    className="bg-gray-300 text-gray-500 px-4 py-2 rounded-md cursor-not-allowed"
                  >
                    Create Project
                  </button>
                ) : (
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Create Project
                  </button>
                );
              })()}
            </div>
            {user?.role === 'designer' && (() => {
              const projectLimit =
                user?.subscriptionTier === 'one_project' ? 1
                : user?.subscriptionTier === 'two_projects' ? 2
                : Infinity;
              return projects.length >= projectLimit ? (
                <p className="text-sm text-gray-500">
                  Need more projects?{' '}
                  <a href="mailto:info@eleveneagles.tech" className="text-blue-600 underline">
                    Contact Us
                  </a>{' '}
                  to upgrade to Unlimited
                </p>
              ) : null;
            })()}
          </div>
        </div>

        {/* Pending collaboration invitations */}
        {pendingInvites.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-amber-700 mb-3">
              Pending Collaboration Invitations ({pendingInvites.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pendingInvites.map((project) => (
                <div
                  key={project._id}
                  className="bg-white rounded-lg shadow-md p-6 border-2 border-amber-300"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{project.name}</h3>
                    <span className="text-xs font-medium bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full whitespace-nowrap ml-2">
                      Invite Pending
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mb-4">
                    You have been invited to collaborate on this project.
                  </div>
                  <div className="flex space-x-2">
                    {atCollabLimit ? (
                      <div className="flex-1">
                        <button
                          disabled
                          className="w-full bg-gray-200 text-gray-400 px-3 py-2 rounded text-sm cursor-not-allowed"
                          title="You've reached your project limit"
                        >
                          Accept
                        </button>
                        <p className="text-xs text-amber-600 mt-1 text-center">
                          Project limit reached — remove a project or{' '}
                          <a href="mailto:info@eleveneagles.tech" className="underline">upgrade your plan</a>
                        </p>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleRespondToInvitation(project._id, true)}
                        disabled={respondingProject === project._id}
                        className="flex-1 bg-indigo-600 text-white px-3 py-2 rounded text-sm hover:bg-indigo-700 transition-colors disabled:opacity-50"
                      >
                        {respondingProject === project._id ? 'Accepting...' : 'Accept'}
                      </button>
                    )}
                    <button
                      onClick={() => handleRespondToInvitation(project._id, false)}
                      disabled={respondingProject === project._id}
                      className="flex-1 bg-gray-200 text-gray-700 px-3 py-2 rounded text-sm hover:bg-gray-300 transition-colors disabled:opacity-50"
                    >
                      Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active projects grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {activeProjects.map((project) => {
            const isOwnerOrAdmin = project.myRole === 'owner' || project.myRole === 'admin';
            const isCollaborator = project.myRole === 'collaborator';
            return (
              <div
                key={project._id}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => handleProjectClick(project)}
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{project.name}</h3>
                  {isCollaborator && (
                    <span className="text-xs font-medium bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full whitespace-nowrap ml-2">
                      Collaborating
                    </span>
                  )}
                </div>

                <div className="space-y-2 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">Location:</span>{' '}
                    {project.location
                      ? `${project.location.latitude.toFixed(4)}, ${project.location.longitude.toFixed(4)}`
                      : 'Not set'}
                  </div>
                  <div>
                    <span className="font-medium">Clients:</span>{' '}
                    {project.clients?.length || 0}
                  </div>
                  <div>
                    <span className="font-medium">Created:</span>{' '}
                    {new Date(project.createdAt).toLocaleDateString()}
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center mb-2">
                    <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                      View Details →
                    </button>
                  </div>
                  {isOwnerOrAdmin && (
                    <div className="flex space-x-1 justify-end">
                      <button
                        onClick={(e) => { e.stopPropagation(); openEditProject(project); }}
                        className="bg-blue-600 text-white px-2 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); openAddClient(project); }}
                        className="bg-green-600 text-white px-2 py-1 rounded text-sm hover:bg-green-700 transition-colors"
                      >
                        Add Client
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteProject(project._id); }}
                        disabled={deletingProjectId === project._id}
                        className="bg-red-600 text-white px-2 py-1 rounded text-sm hover:bg-red-700 transition-colors disabled:opacity-50"
                      >
                        {deletingProjectId === project._id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {projects.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg mb-4">
              {user?.role === 'admin' || user?.role === 'designer'
                ? 'No projects found. Create your first project to get started.'
                : 'No projects assigned to you yet.'}
            </div>
            {(user?.role === 'admin' || user?.role === 'designer') && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors"
              >
                Create First Project
              </button>
            )}
          </div>
        )}

        {/* Create Project Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h2 className="text-xl font-semibold mb-4">Create New Project</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Project Name *
                  </label>
                  <input
                    type="text"
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter project name..."
                    required
                  />
                </div>

                <CourseSearchPicker value={location} onChange={setLocation} />

                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    Enable Project Tracking
                  </label>
                  <input
                    type="checkbox"
                    checked={trackingEnabled}
                    onChange={(e) => setTrackingEnabled(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    Enable Project Management
                  </label>
                  <input
                    type="checkbox"
                    checked={managementEnabled}
                    onChange={(e) => setManagementEnabled(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateProject}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                  disabled={creating || !newProjectName.trim()}
                >
                  {creating ? 'Creating...' : 'Create Project'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Project Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h2 className="text-xl font-semibold mb-4">Edit Project</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Project Name *
                  </label>
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter project name..."
                    required
                  />
                </div>

                <CourseSearchPicker value={editingLocation} onChange={setEditingLocation} />

                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    Enable Project Tracking
                  </label>
                  <input
                    type="checkbox"
                    checked={editingTracking}
                    onChange={(e) => setEditingTracking(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    Enable Project Management
                  </label>
                  <input
                    type="checkbox"
                    checked={editingManagement}
                    onChange={(e) => setEditingManagement(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                </div>

                {/* Collaborating Designers */}
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-700">Collaborating Designers</h3>
                    <button
                      onClick={(e) => { e.preventDefault(); openInviteModal(editingProject!._id); }}
                      className="text-xs bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700 transition-colors"
                    >
                      + Invite Designer
                    </button>
                  </div>
                  {(editingProject?.collaborators?.filter((c) => c.status !== 'declined') || []).length === 0 ? (
                    <p className="text-xs text-gray-400 italic">No collaborators yet.</p>
                  ) : (
                    <ul className="space-y-2">
                      {editingProject?.collaborators
                        ?.filter((c) => c.status !== 'declined')
                        .map((c) => {
                          const uid = getUserId(c);
                          const uname = getUserName(c);
                          return (
                            <li key={uid} className="flex items-center justify-between text-sm">
                              <div>
                                <span className="font-medium text-gray-800">{uname}</span>
                                <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
                                  c.status === 'accepted'
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-amber-100 text-amber-700'
                                }`}>
                                  {c.status === 'accepted' ? 'Accepted' : 'Pending'}
                                </span>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleRemoveCollaborator(editingProject!._id, uid, uname);
                                }}
                                disabled={removingCollaborator === uid}
                                className="text-red-500 hover:text-red-700 text-xs disabled:opacity-50"
                              >
                                {removingCollaborator === uid ? 'Removing...' : 'Remove'}
                              </button>
                            </li>
                          );
                        })}
                    </ul>
                  )}
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                  disabled={editing}
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditProject}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                  disabled={editing || !editingName.trim()}
                >
                  {editing ? 'Updating...' : 'Update Project'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Client Modal */}
        {showClientModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
              <h2 className="text-xl font-semibold mb-6">
                Add Client to {selectedProjectForClient?.name}
              </h2>

              <div className="space-y-6">
                {/* Existing User Search */}
                <div>
                  <h3 className="text-lg font-medium mb-3">Add Existing User</h3>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Select a client to add
                      </label>
                      <select
                        value={selectedExistingUser?._id || ''}
                        onChange={(e) => {
                          const userId = e.target.value;
                          const user = availableUsers.find(u => u._id === userId) || null;
                          setSelectedExistingUser(user);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        disabled={searching}
                      >
                        <option value="">
                          {searching ? 'Loading available clients...' : 'Select a client...'}
                        </option>
                        {availableUsers.map((user) => (
                          <option key={user._id} value={user._id}>
                            {user.name} ({user.email})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <button
                        onClick={handleAddExistingUser}
                        disabled={!selectedExistingUser}
                        className="w-full bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Add Selected Client to Project
                      </button>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-medium mb-3">Or Create New Client</h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name *
                      </label>
                      <input
                        type="text"
                        value={clientForm.name}
                        onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter client name..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        value={clientForm.email}
                        onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter client email..."
                      />
                    </div>


                  </div>

                  <div className="mt-4">
                    <button
                      onClick={handleAddClient}
                      className="w-full bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
                      disabled={addingClient || !clientForm.name.trim() || !clientForm.email.trim()}
                    >
                      {addingClient ? 'Creating Client...' : 'Create & Add New Client'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => {
                    setShowClientModal(false);
                    setSelectedProjectForClient(null);
                    setClientForm({ name: '', email: '' });

                    setAvailableUsers([]);
                    setSelectedExistingUser(null);
                  }}
                  className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-400 transition-colors"
                  disabled={addingClient}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Invite Designer Modal */}
        {showInviteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h2 className="text-xl font-semibold mb-1">Invite Designer to Collaborate</h2>
              <p className="text-sm text-gray-500 mb-4">
                Only designers who have enabled collaboration visibility will appear below.
              </p>

              {loadingDesigners ? (
                <div className="text-center py-8 text-gray-500">Loading designers...</div>
              ) : visibleDesigners.length === 0 ? (
                <div className="text-center py-8 text-gray-400 italic">
                  No designers are currently available for collaboration.
                </div>
              ) : (
                <ul className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
                  {visibleDesigners.map((d) => (
                    <li key={d._id} className="flex items-center justify-between py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{d.name}</p>
                        <p className="text-xs text-gray-500">{d.email}</p>
                      </div>
                      <button
                        onClick={() => handleInviteDesigner(d._id)}
                        disabled={inviting === d._id}
                        className="bg-indigo-600 text-white px-3 py-1 rounded text-sm hover:bg-indigo-700 transition-colors disabled:opacity-50"
                      >
                        {inviting === d._id ? 'Inviting...' : 'Invite'}
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => { setShowInviteModal(false); setInviteProjectId(null); setVisibleDesigners([]); }}
                  className="bg-gray-300 text-gray-700 px-5 py-2 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectScreen;
